import React, { useEffect } from 'react';

import PageNumberBoxes from './PageNumberBoxes';
import { Dropdown } from '../../common-ui/Dropdown/Dropdown';

// The backend can only handle 10k loans at a time
const MAXIMUM_TOTAL_ITEMS = 10000;

const rowsPerPageOptions = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

export interface IPaginationControlsProps {
  currentPage: number;
  pageSize: number;
  totalItems?: number;
  update: (newPage: number, newPageSize: number) => void;
}

const PaginationControls: React.FC<IPaginationControlsProps> = ({
  currentPage,
  pageSize,
  totalItems,
  update,
}) => {
  const constrainedTotalItems = totalItems ? Math.min(totalItems, MAXIMUM_TOTAL_ITEMS) : 0;

  const calculatedTotalPages = constrainedTotalItems
    ? Math.ceil(constrainedTotalItems / pageSize)
    : 0;

  const hasPages = calculatedTotalPages > 1;

  useEffect(() => {
    if (!calculatedTotalPages) {
      return;
    }
    if (currentPage > calculatedTotalPages) {
      const newPage = Math.max(1, calculatedTotalPages);
      if (newPage !== currentPage) {
        update(1, pageSize);
      }
    }
  }, [calculatedTotalPages, currentPage, update, pageSize]);

  const handlePageChange = (newPage: number) => {
    update(newPage, pageSize);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number | undefined) => {
    if (!newRowsPerPage) {
      return;
    }
    update(1, newRowsPerPage);
  };

  if (!hasPages) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2.5 font-heebo text-xs font-normal leading-5 tracking-normal text-gray-600">
          <div>Rows Per Page</div>
          <Dropdown
            menuPlacement="top"
            isMulti={false}
            options={rowsPerPageOptions}
            value={rowsPerPageOptions.find((opt) => opt.value === pageSize)}
            onChange={(option) => handleRowsPerPageChange(option?.value)}
            isDisabled={!calculatedTotalPages}
          />
        </div>
        <div className="font-heebo text-xs font-normal leading-5 text-gray-600">
          (of {totalItems?.toLocaleString()})
        </div>
        <PageNumberBoxes
          currentPage={currentPage}
          totalPages={calculatedTotalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default PaginationControls;
